import { Trans, useTranslation } from "react-i18next";

import { Link, Spacer, Typography, useToast } from "@aviary";
import { gRecaptchaExecute } from "@shared/utils";
import { l } from "@unauthenticated/shared/locales/i18n";

import {
  useResendTwoFactorAuthPasscodeEmailMutation,
  type ResendTwoFactorAuthPasscodeEmailData,
} from "./ResendTwoFactorAuthPasscodeEmail.mutation";

import * as styles from "./ResendEmail.styles";

interface Props {
  token: string;
  email: string;
}

const ResendEmail = ({ token, email }: Props) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();

  const onResendPasscodeEmailComplete = (data: ResendTwoFactorAuthPasscodeEmailData) => {
    const hasErrors = data?.auth?.userResendTwoFactorAuthentication?.errors?.message;
    const isEmailSent = data?.auth?.userResendTwoFactorAuthentication?.sent;

    if (hasErrors || !isEmailSent) {
      return makeToast("error", t(l.signIn.emailNotSent));
    }

    if (isEmailSent) {
      makeToast("success", t(l.signIn.EmailHasBeenSent, { email }));
    }
  };

  const [resendPasscodeEmail, { loading: loadingResendPasscodeEmail }] =
    useResendTwoFactorAuthPasscodeEmailMutation({
      onCompleted: onResendPasscodeEmailComplete,
      onError: ({ message }) => makeToast("error", message),
    });

  const onResendPasscodeEmail = () => {
    if (!loadingResendPasscodeEmail) {
      gRecaptchaExecute(captchaToken => {
        resendPasscodeEmail({
          variables: {
            input: {
              token,
              captchaToken,
            },
          },
        });
      });
    }
  };

  return (
    <div css={styles.wrapper}>
      <Typography type="footnote" isMarginless>
        <Trans i18nKey={l.signIn.DidNotGetEmail}>
          Didn't get the email? Check your spam folder or
          <Link onClick={onResendPasscodeEmail}>resend</Link>.
        </Trans>
      </Typography>
      <Spacer height="half" />
      <Typography type="footnote" css={styles.expirationMessage} isMarginless>
        {t(l.signIn.twoFactorAuthForm.codeWillExpire)}
      </Typography>
    </div>
  );
};

export { ResendEmail };
